import type { IsoDate } from './monthGrid';

/** An `IsoDate` as UTC midnight, so formatting with `timeZone: 'UTC'` never shifts the day. */
const toDate = (iso: IsoDate): Date => {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const dayFormat = (locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat =>
  new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...options });

/** "23 October" / "23. oktober". */
export const formatDay = (iso: IsoDate, locale: string): string =>
  dayFormat(locale, { day: 'numeric', month: 'long' }).format(toDate(iso));

/** "23 October 2026" / "23. oktober 2026". */
export const formatLongDate = (iso: IsoDate, locale: string): string =>
  dayFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(toDate(iso));

/** "October 2026" / "Oktober 2026". */
export const formatMonth = (year: number, month: number, locale: string): string => {
  const text = dayFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(year, month - 1, 1))
  );
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
};

/** `HH:mm` in the viewer's zone. */
export const formatTime = (timestamp: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(timestamp)
  );

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * When a sync last ran, in the viewer's zone: `HH:mm` on `today`, else the short date before it —
 * "14 Oct 14:05" / "14. okt. 14:05".
 */
export const formatSyncedAt = (timestamp: string, today: IsoDate, locale: string): string => {
  const at = new Date(timestamp);
  const day: IsoDate = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
  const time = formatTime(timestamp, locale);
  if (day === today) {
    return time;
  }
  const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(at);
  return `${date} ${time}`;
};
